import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { JourneyLoadingScreen, StandardJourneyBrandedScreen } from '~/components/StandardJourney';
import StandardJourneyFlow from '~/pages/PersonalCoverage/newJourney';
import useStore from '~/store';
import ModalCalcSimplifiedCoverage from '~/modals/CalcSimplifiedCoverage';
import { useGetStepPlans } from '~/modals/CalcSimplifiedCoverage/hooks/use-get-plans';
import { isPromotionUnavailable } from '~/utils/promotionUtils';
import ErrorPage from '../Error';

function LegacyHomePage() {
  const { data, loading, error } = useGetStepPlans();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!data || loading) return;

    const modalParam = searchParams.get('modal');
    const promotionUnavailable = isPromotionUnavailable(data);

    const shouldAutoOpen = modalParam === 'true' || !promotionUnavailable;

    if (shouldAutoOpen) {
      setIsModalOpen(true);
    }
  }, [searchParams, data, loading]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSearchParams({});
  };

  const promotionUnavailable = isPromotionUnavailable(data);

  if (loading) {
    return <div> Loading </div>;
  }

  if (error) {
    return <ErrorPage statusCode={error.statusCode} message={error.message} />;
  }

  return (
    <Container maxWidth="sm">
      {!isModalOpen && !promotionUnavailable && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            textAlign: 'center',
            gap: 2
          }}
        >
          <Typography variant="h5" component="h1" gutterBottom>
            Cálculo de Cobertura
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Calcule sua cobertura de forma rápida e simples
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleOpenModal}
            sx={{
              minWidth: 200,
              padding: '12px 24px',
              fontSize: '1rem'
            }}
          >
            Iniciar Cálculo
          </Button>
        </Box>
      )}

      {promotionUnavailable && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            textAlign: 'center',
            gap: 2
          }}
        >
          <Alert severity="warning" sx={{ width: '100%', maxWidth: '500px' }}>
            <Typography variant="h6" component="div" gutterBottom>
              Promoção Indisponível
            </Typography>
            <Typography variant="body1">
              Esta promoção não está mais disponível. Tente novamente mais tarde ou entre em contato conosco para mais
              informações.
            </Typography>
          </Alert>
        </Box>
      )}

      {isModalOpen && !promotionUnavailable && (
        <>
          {data?.club ? (
            <ModalCalcSimplifiedCoverage
              steps={data?.steps}
              club={data.club}
              distributor={data?.distributor ?? undefined}
              sellerInfo={typeof data?.seller === 'object' && data.seller !== null ? data.seller : undefined}
              isOpen={isModalOpen}
              onClose={handleCloseModal}
            />
          ) : (
            <ModalCalcSimplifiedCoverage
              steps={data?.steps}
              distributor={data?.distributor ?? undefined}
              sellerInfo={typeof data?.seller === 'object' && data.seller !== null ? data.seller : undefined}
              isOpen={isModalOpen}
              onClose={handleCloseModal}
            />
          )}
        </>
      )}
    </Container>
  );
}

/**
 * Entrada da jornada 2026.
 *
 * A tela de boas-vindas («Contratar agora») foi removida: quem abre o link
 * `/?plan=&org=` cai **direto** na identificação por CPF (Figma 1675:3342).
 * Aqui ficam só os estados que precedem o wizard — carregamento dos planos,
 * erro e promoção indisponível. O clube e o vendedor, que a tela antiga
 * mostrava, passaram para a faixa de atribuição dentro do cartão.
 */
function NewHomePage() {
  const { data, loading, error } = useGetStepPlans();
  const promotionUnavailable = isPromotionUnavailable(data);

  // Entrar na jornada começa sempre uma cotação limpa — antes isto acontecia
  // no clique de «Contratar agora».
  useEffect(() => {
    useStore.getState().clearPersistedQuoteDraft();
  }, []);

  if (loading) {
    return (
      <StandardJourneyBrandedScreen
        appId="journey-home-screen"
        contentLayout="fullscreen"
        progressPercent={0}
        showHeaderProgressBar={false}
      >
        <JourneyLoadingScreen appId="journey-home-loading" message="Preparando sua cotação" />
      </StandardJourneyBrandedScreen>
    );
  }

  if (error) {
    return <ErrorPage statusCode={error.statusCode} message={error.message} />;
  }

  if (promotionUnavailable) {
    return (
      <StandardJourneyBrandedScreen
        appId="journey-home-screen"
        contentLayout="fullscreen"
        progressPercent={0}
        showHeaderProgressBar={false}
      >
        <Alert severity="warning" sx={{ maxWidth: 500, width: '100%' }}>
          <Typography component="div" gutterBottom variant="h6">
            Promoção indisponível
          </Typography>
          <Typography variant="body2">
            Esta promoção não está mais disponível. Tente novamente mais tarde ou entre em contato conosco para mais
            informações.
          </Typography>
        </Alert>
      </StandardJourneyBrandedScreen>
    );
  }

  return <StandardJourneyFlow />;
}

export default function HomePage() {
  const [searchParams] = useSearchParams();
  const legacyMode = searchParams.get('modal') === 'true' || searchParams.get('legacy') === 'true';

  return legacyMode ? <LegacyHomePage /> : <NewHomePage />;
}
